// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — Reactivated
// Path: src/components/dashboard/pollen/PollenReactivated.jsx
//
// "They came back. Respond now."
// Surfaces donors who had a 180+ day gap with no gifts,
// then gave again recently (last 90 days). Unexpected pollen —
// the most valuable kind.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { RefreshCw, X, Sprout } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const daysBetween = (dateA, dateB) => {
  const a = new Date(dateA + "T12:00:00");
  const b = new Date(dateB + "T12:00:00");
  return Math.floor(Math.abs(b - a) / (1000 * 60 * 60 * 24));
};

export const PollenReactivated = ({ onClose }) => {
  const { palette, navigate, dataVersion, members, cultivationQueue } = useApp();
  const [reactivated, setReactivated] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        const cutoffStr = cutoff.toISOString().split("T")[0];

        // Paginate through ALL gifts to build complete history per member
        const giftsByMember = {};
        const PAGE_SIZE = 1000;
        let offset = 0;
        while (true) {
          const { data: page, error: err } = await supabase
            .from("gifts")
            .select("member_id, gift_date, amount")
            .order("gift_date", { ascending: true })
            .range(offset, offset + PAGE_SIZE - 1);

          if (err || !page) break;
          page.forEach((gift) => {
            const memberId = gift.member_id;
            if (!giftsByMember[memberId]) {
              giftsByMember[memberId] = [];
            }
            giftsByMember[memberId].push({
              date: gift.gift_date,
              amount: gift.amount,
            });
          });
          if (page.length < PAGE_SIZE) break;
          offset += PAGE_SIZE;
        }

        // Find reactivated: 180+ day gap between consecutive gifts,
        // and the most recent gift is within the last 90 days
        const results = [];

        Object.entries(giftsByMember).forEach(([memberId, gifts]) => {
          if (gifts.length < 2) return; // Need at least 2 gifts to detect a gap

          const lastGift = gifts[gifts.length - 1];
          if (lastGift.date < cutoffStr) return;

          // Check for any 180+ day gap between consecutive gifts
          let longestGap = 0;
          for (let idx = 1; idx < gifts.length; idx++) {
            const gap = daysBetween(gifts[idx - 1].date, gifts[idx].date);
            if (gap > longestGap) {
              longestGap = gap;
            }
          }

          if (longestGap >= 180) {
            results.push({
              memberId,
              lastGiftDate: lastGift.date,
              lastGiftAmount: lastGift.amount,
              gapDays: longestGap,
              totalGifts: gifts.length,
            });
          }
        });

        // Sort by most recent gift first
        results.sort((a, b) => b.lastGiftDate.localeCompare(a.lastGiftDate));
        setReactivated(results.slice(0, 8));
      } catch (err) {
        // silent
      }
    };
    load();
  }, [dataVersion]);

  const getDonorName = (donor) => {
    const member = members.find((m) => m.id === donor.memberId);
    if (!member) return "—";
    return (
      member.credentialedFullName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "—"
    );
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <RefreshCw size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            Reactivated
          </span>
          {reactivated.length > 0 && (
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: FONT_FAMILY,
              }}
            >
              {reactivated.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
        </button>
      </div>

      {/* Subtitle */}
      <div
        style={{
          padding: "8px 16px 4px",
          fontSize: "0.65rem",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          fontStyle: "italic",
        }}
      >
        They came back after a long absence. Respond now.
      </div>

      {/* Donor list */}
      {reactivated.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No reactivated donors right now.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", padding: "4px 0" }}>
          {reactivated.map((donor) => (
            <div
              key={donor.memberId}
              onClick={() => {
                const fullMember = members.find((m) => m.id === donor.memberId);
                if (fullMember) navigate("memberDetail", fullMember);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 16px",
                cursor: members.find((m) => m.id === donor.memberId) ? "pointer" : "default",
                transition: "background 0.1s",
              }}
              onMouseEnter={(event) => {
                if (donor.members) event.currentTarget.style.background = `${palette.accent1}08`;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getDonorName(donor)}
                </div>
              </div>
              {cultivationQueue.some((q) => q.memberId === donor.memberId) && (
                <Sprout size={ICON_SIZES.xs} style={{ color: "#10b981", flexShrink: 0 }} title="In cultivation queue" />
              )}
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  flexShrink: 0,
                }}
              >
                {donor.gapDays}d gap
              </span>
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: "#8b5cf6",
                  fontFamily: FONT_FAMILY,
                  flexShrink: 0,
                }}
              >
                {fmtCurrency(donor.lastGiftAmount)}
              </span>
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  flexShrink: 0,
                }}
              >
                {fmtDate(donor.lastGiftDate)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
